import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { createClientOnlyFn } from '@tanstack/react-start';
import Cookies from 'js-cookie';
import { siteMetadata } from '../types/meta';

// Analytics (used to be gatsby-plugin-gdpr-tracking and gatsby-browser.tsx in the old project):
// - Google Analytics 4 and Hotjar only start once the visitor accepts cookies (the cookie consent banner, see
//   routes/_layout.tsx), their scripts aren't even loaded before that.
// - Application Insights (page views, errors, performance, for debugging) always tracks, but only stores anything on
//   the visitor's device (cookies, session storage) with consent.
// Tracking runs in production builds only, on the production domain only (see isTrackingEnabled), and each service
// only if its env variable is set.
// Page views of client-side navigations are tracked by the services themselves (GA4 enhanced measurement "page
// changes based on browser history events", Hotjar, and Application Insights' enableAutoRouteTracking).

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    hj?: ((...args: unknown[]) => void) & { q?: unknown[] };
    _hjSettings?: { hjid: number; hjsv: number };
    [gaDisable: `ga-disable-${string}`]: boolean | undefined;
  }
}

/** Name of the cookie consent cookie ('true' if accepted, 'false' if declined), set by react-cookie-consent. */
export const consentCookieName = 'cookie-consent';

const {
  VITE_GA_MEASUREMENT_ID: gaMeasurementId,
  VITE_HOTJAR_ID: hotjarId,
  VITE_HOTJAR_SNIPPET_VERSION: hotjarSnippetVersion = '6',
  VITE_APPLICATIONINSIGHTS_CONNECTION_STRING: appInsightsConnectionString
} = import.meta.env;

// The production domain (with and without "www."): local builds (pnpm build, pnpm preview) and the CI's preview
// environments use the production settings too, but they must not send data to the real analytics services.
const productionHostname = new URL(siteMetadata.siteUrl).hostname;
const trackedHostnames = [
  productionHostname,
  productionHostname.replace(/^www\./, '')
];

function isTrackingEnabled() {
  return (
    import.meta.env.PROD && trackedHostnames.includes(window.location.hostname)
  );
}

/** Google Analytics' opt-out flag (window property and cookie), https://developers.google.com/analytics/devguides/collection/ga4/disable-analytics */
const gaDisableKey = `ga-disable-${gaMeasurementId}` as const;

let isGoogleAnalyticsStarted = false;
let isHotjarStarted = false;
let appInsights: Promise<ApplicationInsights> | null = null;

function hasConsent() {
  return Cookies.get(consentCookieName) === 'true';
}

function loadScript(src: string) {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function startGoogleAnalytics() {
  if (!gaMeasurementId || isGoogleAnalyticsStarted) {
    return;
  }

  isGoogleAnalyticsStarted = true;

  // re-enable it, if the visitor opted out earlier (see gaOptOut) and accepted cookies again since
  Cookies.remove(gaDisableKey, { path: '/' });
  window[gaDisableKey] = false;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // gtag.js requires the `arguments` object itself
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', gaMeasurementId);

  loadScript(`https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`);
}

function startHotjar() {
  if (!hotjarId || isHotjarStarted) {
    return;
  }

  isHotjarStarted = true;

  // Hotjar's tracking code (https://help.hotjar.com/hc/en-us/articles/115009336727)
  window.hj =
    window.hj ??
    function hj() {
      (window.hj!.q = window.hj!.q ?? []).push(arguments);
    };
  window._hjSettings = {
    hjid: Number(hotjarId),
    hjsv: Number(hotjarSnippetVersion)
  };

  loadScript(
    `https://static.hotjar.com/c/hotjar-${hotjarId}.js?sv=${hotjarSnippetVersion}`
  );
}

/**
 * Config of the Application Insights channel (the SDK's BreezeChannelIdentifier), which buffers and sends the
 * telemetry. Its buffer settings must be set here: a root level value is only copied to it at initialization.
 */
function appInsightsChannelConfig(enableSessionStorageBuffer: boolean) {
  return {
    extensionConfig: {
      AppInsightsChannelPlugin: { enableSessionStorageBuffer }
    }
  };
}

function startApplicationInsights() {
  if (!appInsightsConnectionString || appInsights) {
    return;
  }

  // loaded on demand, it's a big package
  appInsights = import('@microsoft/applicationinsights-web').then(
    ({ ApplicationInsights }) => {
      const instance = new ApplicationInsights({
        config: {
          connectionString: appInsightsConnectionString,
          // tracks the page views of client-side navigations
          enableAutoRouteTracking: true,
          // Chrome deprecates the `unload` event (it logs a permissions policy violation), the telemetry is still sent
          // on leaving the page through the "pagehide" and "visibilitychange" events
          disablePageUnloadEvents: ['unload'],
          // Without consent nothing is stored on the visitor's device:
          // - no ai_user and ai_session cookies
          // - unsent telemetry is kept in memory instead of session storage (AI_buffer, AI_sentBuffer), so it's
          //   only lost if the page is closed before it's sent (it's sent every 15 seconds, and when leaving the page)
          disableCookiesUsage: !hasConsent(),
          ...appInsightsChannelConfig(hasConsent())
        }
      });

      instance.loadAppInsights();
      instance.trackPageView();

      return instance;
    }
  );
}

/** Starts the tracking on page load (once, in the layout): the services the visitor consented to. */
export const initTracking = createClientOnlyFn(() => {
  if (!isTrackingEnabled()) {
    return;
  }

  startApplicationInsights();

  if (hasConsent()) {
    startGoogleAnalytics();
    startHotjar();
  }
});

/** Starts the rest of the tracking when the visitor accepts cookies. */
export const startTrackingWithConsent = createClientOnlyFn(() => {
  if (!isTrackingEnabled()) {
    return;
  }

  startGoogleAnalytics();
  startHotjar();
  appInsights
    ?.then(instance => {
      instance.getCookieMgr().setEnabled(true);
      // the pending telemetry is moved from memory to session storage
      instance.updateCfg(appInsightsChannelConfig(true));
    })
    .catch(error =>
      console.error('Failed to enable Application Insights storage', error)
    );
});

/** Opts out from Google Analytics (used when the visitor revokes the cookie consent). */
export const gaOptOut = createClientOnlyFn(() => {
  if (!gaMeasurementId) {
    return;
  }

  Cookies.set(gaDisableKey, 'true', { path: '/', expires: 3650 });
  window[gaDisableKey] = true;
});
